import { TuiStatus } from '@taiga-ui/kit';
import { pure } from '@core/helpers/pure';
import { TuiInput, TuiTextfield } from '@taiga-ui/core';
import {
  Component,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { IconButtonComponent } from '@common/components';
import { RequestProductIconComponent } from '@features/requests/request-product-icon/request-product-icon.component';
import { TranslatePipe } from '@ngx-translate/core';
import {
  TuiAccordion,
  TuiUnmaskHandler,
  TuiBadge,
  TuiInputPhone,
} from '@taiga-ui/kit';

import { TuiNotification, TuiError, TuiIcon, TuiHint } from '@taiga-ui/core';
import { RequestProductIconSize } from '@features/requests/request-product-icon/types';
import { Product, RequestDevice } from '@core/types';
import { DeviceDetectorService } from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { RequestService } from '@core/services/request/request.service';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RequestModel } from '@core/models';
import { Nullable } from '@core/helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceInstallQualityCheckStatus } from '@core/types/DeviceInstallQuality';
import { EmptyBlockComponent } from '@core/components';
import { AccountService } from '@core/services/account';
import { EntityTranslatePipe } from '@core/pipes';
import { getAlternateInstallerBannerState } from '@features/requests/request-form/helpers/alternate-installer-banner.helper';
import {
  DeviceTamperStoreService,
  TamperState,
} from '@features/requests/request-form/services/device-tamper-store.service';

@Component({
  selector: 'x-goods-block',
  imports: [
    TuiStatus,
    TuiTextfield,
    AsyncPipe,
    IconButtonComponent,
    RequestProductIconComponent,
    TranslatePipe,
    EntityTranslatePipe,
    TuiBadge,
    TuiError,
    TuiInput,
    TuiIcon,
    TuiHint,
    NgClass,
    ReactiveFormsModule,
    EmptyBlockComponent,
    TuiInputPhone,
    TuiUnmaskHandler,
    TuiAccordion,
    TuiNotification,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './goods-block.component.html',
})
export class GoodsBlockComponent {
  private static readonly DEVICE_PRODUCT_IDS = [2, 3, 4, 10, 21, 22, 38, 53];
  $request = input.required<RequestModel>({ alias: 'request' });

  $requestDevices = input.required<Nullable<RequestDevice[]>>({
    alias: 'requestDevices',
  });

  $devicesInstallQuality = input.required<
    Nullable<
      Record<RequestDevice['deviceHash'], DeviceInstallQualityCheckStatus>
    >
  >({ alias: 'devicesInstallQuality' });

  public deviceAddForm: Record<Product['id'], boolean> = {};
  public productDevicesInfo: Partial<
    Record<RequestDevice['deviceModelId'], RequestDevice[]>
  > = {};

  private readonly formBuilder = inject(FormBuilder);
  public addDeviceFormGroup: Record<string, FormGroup> | null = null;

  public readonly RequestProductIconSize = RequestProductIconSize;

  private readonly requestService = inject(RequestService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly deviceTamperStore = inject(DeviceTamperStoreService);
  public readonly permissionService = inject(OutkeepPermissionService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly accountService = inject(AccountService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly document = inject(DOCUMENT);

  protected readonly $isMobile = this.deviceService.$isMobile;

  readonly $currentUser = this.accountService.$currentUser;
  public $isCurrentUserAlternateInstaller = signal(false);
  public $currentUserAlternateInstallerProducts = signal('');

  readonly simMask = {
    mask: [
      /\d/,
      ' ',
      /\d/,
      /\d/,
      /\d/,
      ' ',
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
    ],
  };
  readonly unmaskSim = (value: string): string => value.replace(/[-\s]/g, '');
  constructor() {
    effect(() => {
      if (this.$requestDevices() && this.$request().products) {
        this.parseRequestProducts();
        this.cdr.markForCheck();
      }
    });
  }

  private parseRequestProducts() {
    const products = this.$request().products;
    const devices = this.$requestDevices() ?? [];
    const alternateInstallerBannerState = getAlternateInstallerBannerState(
      products,
      this.$currentUser()?.personId?.id,
    );

    this.$isCurrentUserAlternateInstaller.set(
      alternateInstallerBannerState.isAlternateInstaller,
    );
    this.$currentUserAlternateInstallerProducts.set(
      alternateInstallerBannerState.productsLabel,
    );

    if (!products?.length) {
      return;
    }
    this.deviceAddForm = {};
    this.productDevicesInfo = {};

    const productFormGroups: Record<string, FormGroup> = {};

    for (const product of products) {
      if (!this.productIsDevice(product.requestProductId.id)) {
        continue;
      }

      this.deviceAddForm[product.requestProductId.id] = false;

      const devicesArray = devices.filter(
        d => d.deviceModelId === product.requestProductId.deviceModel,
      );

      if (product.requestProductId.deviceModel !== null) {
        const deviceModelId = product.requestProductId
          .deviceModel as RequestDevice['deviceModelId'];

        this.productDevicesInfo[deviceModelId] = devicesArray;
      }

      // create form group for each product
      productFormGroups[product.requestProductId.id] = this.formBuilder.group({
        sim1: this.formBuilder.control<Nullable<string>>(null, [
          Validators.required,
          Validators.pattern('^[0-9]*$'),
        ]),
      });
    }

    this.addDeviceFormGroup = productFormGroups;
  }

  public toggleAddDeviceForm(requestProductId: Product['id']) {
    this.deviceAddForm[requestProductId] =
      !this.deviceAddForm[requestProductId];
  }

  public performAddNewDeviceToRequest(requestProductId: Product['id']) {
    const addDeviceFormGroup = this.addDeviceFormGroup;

    if (!addDeviceFormGroup) {
      return;
    }

    const form = addDeviceFormGroup[requestProductId];

    if (!form) {
      return;
    }

    if (form.valid) {
      this.requestDevicesService
        .addNewDeviceToRequest$(
          {
            name: form.value.sim1,
            installDate: new Date(),
            secondSim: null,
            hasProtectCase: null,
            protectCaseNumber: null,
          },
          this.$isMobile(),
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.requestDevicesService.refreshEntities();
          this.requestService.reloadRequest();
          this.deviceAddForm[requestProductId] = false;
          form.reset();
          this.cdr.markForCheck();
        });
    }
  }

  getAddProductDeviceForm(requestProductId: Product['id']) {
    return this.addDeviceFormGroup?.[requestProductId.toString()];
  }

  @pure
  public productIsDevice(requestProductId: Product['id']): boolean {
    return GoodsBlockComponent.DEVICE_PRODUCT_IDS.includes(requestProductId);
  }

  public getDevicesByModelId(
    deviceModelId: Product['deviceModel'],
  ): RequestDevice[] {
    if (deviceModelId === null) {
      return [];
    }

    return (
      this.productDevicesInfo[
        deviceModelId as RequestDevice['deviceModelId']
      ] ?? []
    );
  }

  scrollToDevicesBlock() {
    const devicesBlock = this.document.getElementById('devices');

    if (devicesBlock) {
      devicesBlock.scrollIntoView({ behavior: 'smooth' });
    }
  }

  @pure
  getDevice(sim1: RequestDevice['name']): RequestDevice | undefined {
    return this.$requestDevices()?.find(d => d.name === sim1);
  }

  getTamperState(deviceHash: RequestDevice['deviceHash']): TamperState {
    return this.deviceTamperStore.getByHash(deviceHash);
  }
}
